import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTheme } from '@mui/material';

import { Permission } from '@/entities/permissions';
import { Highcharts } from '@/lib/highcharts';
import usePermission from '@/shared/hooks/usePermission';

import { applyFullscreenYAxisLayout, installFullscreenYAxisSync } from './fullScreenChartAxis';
import {
  createFullScreenStockOptions,
  withoutInitialRangeSelection
} from './fullScreenChartOptions';
import {
  applyFullscreenRangeSelectorLayout,
  installFullscreenRangeSelectorSync
} from './fullScreenChartRangeSelector';
import {
  createFullScreenLegendSeries,
  getSeriesId,
  preserveRuntimeLinkedSeries
} from './fullScreenChartSeries';
import {
  createFullScreenRolloverTooltip,
  useFullScreenRolloverFormatter
} from './fullScreenChartTooltip';

import type HighchartsType from 'highcharts/highstock';

type UseFullScreenChartParams = {
  chartId: string;
  chartSeries: import('./fullScreenChartTypes').CreateFullScreenStockOptionsParams['chartSeries'];
};

const useFullScreenChart = ({ chartId, chartSeries }: UseFullScreenChartParams) => {
  const { hasPermission } = usePermission();
  const theme = useTheme();
  const useSmallWatermark = hasPermission(Permission.UseSmallChartWatermark);
  const createTooltipFormatter = useFullScreenRolloverFormatter();

  const tooltip = useMemo(
    () => createFullScreenRolloverTooltip(createTooltipFormatter, theme.palette.text.primary),
    [createTooltipFormatter, theme.palette.text.primary]
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<HighchartsType.Chart | null>(null);
  const chartIdRef = useRef<string | null>(null);
  const [hiddenSeriesIds, setHiddenSeriesIds] = useState<string[]>([]);

  const availableSeriesIds = useMemo(
    () => new Set(chartSeries.map((series, index) => getSeriesId(series, index))),
    [chartSeries]
  );
  const activeHiddenSeriesIds = useMemo(
    () => hiddenSeriesIds.filter(id => availableSeriesIds.has(id)),
    [availableSeriesIds, hiddenSeriesIds]
  );

  const options = useMemo(
    () =>
      createFullScreenStockOptions({
        chartSeries,
        hiddenSeriesIds: activeHiddenSeriesIds,
        tooltip,
        useSmallWatermark
      }),
    [activeHiddenSeriesIds, chartSeries, tooltip, useSmallWatermark]
  );

  const legendSeries = useMemo(
    () => createFullScreenLegendSeries({ chartSeries, hiddenSeriesIds: activeHiddenSeriesIds }),
    [activeHiddenSeriesIds, chartSeries]
  );

  useEffect(() => {
    if (!containerRef.current) return;

    if (!chartRef.current || chartIdRef.current !== chartId) {
      chartRef.current?.destroy();
      chartRef.current = Highcharts.stockChart(containerRef.current, options);
      chartIdRef.current = chartId;
    } else {
      const updateOptions = preserveRuntimeLinkedSeries(
        withoutInitialRangeSelection(options),
        chartRef.current
      );
      chartRef.current.update(updateOptions, true, true);
    }

    installFullscreenYAxisSync(chartRef.current);
    installFullscreenRangeSelectorSync(chartRef.current);
    applyFullscreenYAxisLayout(chartRef.current, true);
    applyFullscreenRangeSelectorLayout(chartRef.current);
  }, [chartId, options]);

  useEffect(
    () => () => {
      chartRef.current?.destroy();
      chartRef.current = null;
      chartIdRef.current = null;
    },
    []
  );

  const toggleSeriesVisibility = useCallback((seriesId: string) => {
    setHiddenSeriesIds(prev =>
      prev.includes(seriesId) ? prev.filter(id => id !== seriesId) : [...prev, seriesId]
    );
  }, []);

  return { containerRef, legendSeries, toggleSeriesVisibility };
};

export default useFullScreenChart;
